/**
 * The audit packet (FR-21, §9) and the full export (FR-22).
 *
 * Download is a button, not a link: it mints a 15-minute address on click. A
 * packet is one file containing the household's medical history in index form,
 * and an address sitting in browser history or a chat message is exactly what
 * §9 forbids.
 *
 * A packet is a snapshot (FR-56): it shows each record as it stood when the
 * packet was built. That, and how to rebuild one with no app running, is in
 * docs/audit-packet.md rather than on this screen.
 */
import { useState } from 'react';
import { api } from '../api.js';
import { getAccessToken } from '../auth.js';
import { useApi, useAction } from '../hooks.js';
import { ErrorBox, PageHead, Pill, Skeleton, formatDate, toast } from '../ui.js';
import type { Packet } from '../types.js';

export function Packets() {
  const list = useApi<{ packets: Packet[] }>('/api/packets');
  const [kind, setKind] = useState<'tax_year' | 'date_range'>('tax_year');
  const [taxYear, setTaxYear] = useState(new Date().getFullYear() - 1);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const create = useAction(async () => {
    const body = kind === 'tax_year' ? { kind, tax_year: taxYear } : { kind, start: from, end: to };
    await api.post('/api/packets', body);
    toast('Building. It takes a minute or two.');
    list.reload();
  });

  const download = useAction(async (packetId: string) => {
    const res = await api.get<{ url: string }>(`/api/packets/${packetId}/url`);
    window.location.assign(res.url);
  });

  const csv = useAction(downloadCsv);
  const packets = list.data?.packets ?? [];
  const building = packets.some((p) => p.status === 'building');

  return (
    <>
      <PageHead title="Audit packet">
        One ZIP to hand over: a cover sheet, an index of every expense, and every document.
      </PageHead>

      <div className="card stack">
        <div className="chips" role="group" aria-label="What to include">
          <button className="chip" aria-pressed={kind === 'tax_year'} onClick={() => setKind('tax_year')}>
            A tax year
          </button>
          <button className="chip" aria-pressed={kind === 'date_range'} onClick={() => setKind('date_range')}>
            A date range
          </button>
        </div>

        {kind === 'tax_year' ? (
          <div className="field" style={{ maxWidth: 200 }}>
            <label htmlFor="ty">Tax year</label>
            <input id="ty" type="number" value={taxYear} onChange={(e) => setTaxYear(Number(e.target.value))} />
          </div>
        ) : (
          <div className="fields two">
            <div className="field">
              <label htmlFor="from">From</label>
              <input id="from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="to">To</label>
              <input id="to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
          </div>
        )}

        {create.error ? <ErrorBox error={create.error} /> : null}
        <div className="actions">
          <button
            className="primary"
            onClick={() => void create.run()}
            disabled={create.pending || (kind === 'date_range' && (!from || !to))}
          >
            {create.pending ? 'Starting…' : 'Build a packet'}
          </button>
        </div>
      </div>

      {list.error ? <ErrorBox error={list.error} /> : null}
      {list.loading && !list.data ? <Skeleton rows={2} /> : null}

      {packets.length > 0 ? (
        <>
          <h2 className="section">
            <span>Built packets</span>
            {building ? (
              <button className="quiet" onClick={list.reload}>
                Check again
              </button>
            ) : null}
          </h2>
          <div className="list">
            {packets.map((p) => (
              <div className="item plain" key={p.packet_id} title={p.content_hash ? `Content hash ${p.content_hash}` : undefined}>
                <span>
                  <div className="title">{describeScope(p.scope)}</div>
                  <div className="sub">
                    Built {formatDate(p.generated_at, { year: 'always' })} · {p.counts.included} expense
                    {p.counts.included === 1 ? '' : 's'}
                    {p.counts.excluded + p.counts.void > 0
                      ? ` · ${p.counts.excluded + p.counts.void} left out as not claimed`
                      : ''}
                    {p.error ? ` · ${p.error}` : ''}
                  </div>
                </span>
                <span className="end">
                  {p.status === 'ready' ? (
                    <button onClick={() => void download.run(p.packet_id)} disabled={download.pending}>
                      Download
                    </button>
                  ) : p.status === 'failed' ? (
                    <Pill tone="stop">Failed</Pill>
                  ) : (
                    <Pill tone="warn">Building</Pill>
                  )}
                </span>
              </div>
            ))}
          </div>
          <p className="xs muted">Each packet shows the records as they stood when it was built.</p>
        </>
      ) : null}
      {download.error ? <ErrorBox error={download.error} /> : null}

      <h2 className="section">
        <span>Everything, as a spreadsheet</span>
      </h2>
      <div className="card stack tight">
        <p className="small muted">Every record in one CSV file, including the ones not being claimed.</p>
        <div className="actions">
          <button onClick={() => void csv.run()} disabled={csv.pending}>
            {csv.pending ? 'Preparing…' : 'Download CSV'}
          </button>
        </div>
        {csv.error ? <ErrorBox error={csv.error} /> : null}
      </div>
    </>
  );
}

function describeScope(scope: Record<string, unknown>): string {
  if (scope.kind === 'tax_year') return `Tax year ${String(scope.tax_year)}`;
  if (scope.kind === 'date_range') return `${String(scope.start)} to ${String(scope.end)}`;
  if (scope.kind === 'distribution') return 'One withdrawal';
  return 'Packet';
}

/** FR-22 — the CSV export streams through the API rather than S3, so it needs
 *  the bearer token; a plain link would 401. */
async function downloadCsv(): Promise<void> {
  const token = await getAccessToken();
  const res = await fetch('/api/export.csv', { headers: { authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(`The export failed (${res.status}).`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `hsa-expenses-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
